import React, { useState } from "react";
import { Link } from "react-router-dom";

const sections = [
  {
    id: "data",
    icon: "🔒",
    title: "Your Data, Your Control",
    items: [
      "We never sell, rent, or share your personal data with third parties for marketing purposes.",
      "All data you submit to our tools is processed ephemerally — it is not stored beyond your active session unless you explicitly save a run to your dashboard.",
      "You can delete your account and all associated data at any time from your Settings page. Deletion is permanent and processed within 30 days.",
      "We do not use your tool inputs to train AI or machine learning models.",
    ],
  },
  {
    id: "security",
    icon: "🛡️",
    title: "Security Practices",
    items: [
      "All data in transit is encrypted using TLS 1.3. At-rest data in our database is encrypted by Cloudflare's D1 infrastructure.",
      "Passwords are hashed using PBKDF2 with 100,000 iterations and a cryptographically random salt — we never store plain-text passwords.",
      "Authentication uses signed JWTs with short expiration windows, validated against a server-side session store on every request.",
      "API rate limiting is enforced at the edge to prevent abuse. Automated credential stuffing attempts are blocked at the network layer.",
      "We perform dependency audits and apply security patches on a rolling basis.",
    ],
  },
  {
    id: "infrastructure",
    icon: "⚡",
    title: "Infrastructure & Reliability",
    items: [
      "9nd.org runs on Cloudflare Workers — a globally distributed edge compute network with data centers in 200+ cities. Your requests are served from the location closest to you.",
      "Our database (Cloudflare D1) is replicated across Cloudflare's global network with automatic failover.",
      "File storage uses Cloudflare R2, which is S3-compatible and built for high durability.",
      "Payments are processed exclusively by Stripe — PCI DSS Level 1 certified. We never receive, store, or log raw card data.",
      "We target 99.9% uptime. Cloudflare Workers' SLA with redundant global deployment provides the backbone for this commitment.",
    ],
  },
  {
    id: "privacy",
    icon: "👁️",
    title: "Privacy-First by Design",
    items: [
      "We collect the minimum data necessary to operate the service: email address, name, and usage telemetry for aggregate analytics (no personal identifiers in telemetry).",
      "We do not use third-party ad tracking, behavioral profiling cookies, or cross-site tracking scripts.",
      "Our analytics are aggregated and anonymized — we can see that 500 people used the Budget Planner today, but not which 500.",
      "We are committed to GDPR principles: lawful basis for processing, data minimization, right of access, and right to erasure. Users in the EU can request a data export or deletion via email.",
      "Cookie usage is limited to functional session cookies required for authentication. No third-party ad cookies are placed.",
    ],
  },
  {
    id: "ai",
    icon: "🤖",
    title: "Responsible AI",
    items: [
      "Our AI-assisted tools use established models from reputable providers. We do not build or deploy custom large language models trained on user data.",
      "AI-generated outputs are clearly labeled as recommendations, not professional financial, legal, or medical advice.",
      "We apply output filters to prevent generation of harmful, offensive, or misleading content from our tools.",
      "Users retain full ownership of any output generated using our tools. We make no claim to content you produce.",
    ],
  },
  {
    id: "compliance",
    icon: "📋",
    title: "Compliance & Legal",
    items: [
      "9nd.org is operated in compliance with applicable U.S. federal and state laws, and with GDPR principles for users in the European Union.",
      "Our full Privacy Policy and Terms of Service are written in plain language — not legalese — and are publicly accessible at all times.",
      "We maintain an audit log of administrative actions on our platform for internal accountability.",
      "Enterprise customers can request a Data Processing Agreement (DPA) at [email protected].",
    ],
  },
];

const faqs = [
  {
    q: "Where is my data stored?",
    a: "Your data is stored in Cloudflare D1, a globally-replicated SQLite database at the edge. Cloudflare's infrastructure spans 200+ locations. We do not operate our own servers.",
  },
  {
    q: "Do you share my data with AI providers?",
    a: "When you use an AI-powered tool, your input is sent to the relevant AI model API (e.g. for text summarization). This data is subject to that provider's API data policy. We do not permit providers to use your data for model training under our agreements.",
  },
  {
    q: "How do I delete my account?",
    a: "Go to Settings → Account → Delete Account. This permanently removes your account, all saved runs, billing records, and personal data within 30 days. This action cannot be undone.",
  },
  {
    q: "What happens to my data if I cancel my subscription?",
    a: "Your data remains intact on the Free plan. You retain access to your saved runs. You can export or delete your data at any time.",
  },
  {
    q: "Is 9nd.org SOC 2 certified?",
    a: "We are a growing platform and are working toward formal SOC 2 Type II certification. In the meantime, our security practices align with SOC 2 controls, and enterprise customers can request a security questionnaire response.",
  },
  {
    q: "How do I report a security vulnerability?",
    a: "Please email [email protected] with details of the potential issue. We take all reports seriously and aim to acknowledge within 24 hours.",
  },
];

export default function Trust() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 rounded-full px-4 py-1.5 text-sm font-semibold mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block animate-pulse" />
            Privacy-First Platform
          </div>
          <h1 className="text-5xl font-extrabold mb-6 leading-tight">
            Trust &amp; Security
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            We believe security and transparency are not optional. Here is exactly how we handle your data, secure our infrastructure, and maintain your trust.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-slate-300">
            <span className="flex items-center gap-1.5">✅ No data selling, ever</span>
            <span className="flex items-center gap-1.5">✅ TLS 1.3 encryption in transit</span>
            <span className="flex items-center gap-1.5">✅ Edge-native on Cloudflare</span>
            <span className="flex items-center gap-1.5">✅ GDPR-aligned</span>
          </div>
        </div>
      </section>

      {/* ── Quick trust indicators ── */}
      <section className="border-b border-gray-100 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { icon: "🌍", label: "200+ Edge Locations", sub: "Cloudflare global network" },
            { icon: "🔐", label: "TLS 1.3 Encrypted", sub: "All data in transit" },
            { icon: "💳", label: "Stripe Payments", sub: "PCI DSS Level 1" },
            { icon: "🗑️", label: "Right to Erasure", sub: "Delete anytime, 30 days" },
          ].map((item) => (
            <div key={item.label} className="py-4">
              <div className="text-3xl mb-2">{item.icon}</div>
              <p className="font-bold text-gray-900 text-sm">{item.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{item.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Security sections ── */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 space-y-14">
          {sections.map((s) => (
            <div key={s.id} id={s.id}>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">{s.icon}</span>
                <h2 className="text-2xl font-bold text-gray-900">{s.title}</h2>
              </div>
              <ul className="space-y-4">
                {s.items.map((item, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-1 shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">✓</span>
                    <p className="text-gray-600 leading-relaxed">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── Infrastructure logos ── */}
      <section className="py-16 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-8">Built on trusted, audited infrastructure</p>
          <div className="flex flex-wrap justify-center gap-8 items-center">
            {[
              { name: "Cloudflare Workers", badge: "Edge Compute" },
              { name: "Cloudflare D1", badge: "Database" },
              { name: "Cloudflare R2", badge: "Storage" },
              { name: "Stripe", badge: "Payments · PCI DSS L1" },
            ].map((p) => (
              <div key={p.name} className="flex flex-col items-center gap-1 px-6 py-4 bg-white rounded-xl border border-gray-200 shadow-sm min-w-[140px]">
                <p className="font-bold text-gray-800 text-sm">{p.name}</p>
                <p className="text-xs text-indigo-600 font-medium">{p.badge}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">Security FAQ</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <button
                  className="w-full text-left px-6 py-4 font-semibold text-gray-800 flex justify-between items-center hover:bg-gray-50 transition text-sm"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {faq.q}
                  <span className="text-indigo-500 text-xl font-light ml-4 shrink-0">{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-gray-500 text-sm leading-relaxed border-t border-gray-100 pt-3">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section className="py-16 bg-indigo-50 border-t border-indigo-100">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions about security or privacy?</h2>
          <p className="text-gray-600 mb-6">
            Our security team responds to all inquiries within one business day. For vulnerability reports, please include as much detail as possible.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="mailto:[email protected]"
              className="inline-block bg-indigo-600 text-white font-semibold px-7 py-3 rounded-xl hover:bg-indigo-700 transition shadow-sm"
            >
              Email Security Team →
            </a>
            <Link
              to="/consulting"
              className="inline-block border border-indigo-200 text-indigo-700 font-semibold px-7 py-3 rounded-xl hover:bg-white transition"
            >
              Enterprise Inquiries
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-indigo-600 underline underline-offset-2">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-indigo-600 underline underline-offset-2">Terms of Service</Link>
            <a href="mailto:[email protected]" className="hover:text-indigo-600 underline underline-offset-2">Data Deletion Request</a>
          </div>
        </div>
      </section>

    </div>
  );
}
